/**
 * Query invalidation helpers — connect WebSocket events to TanStack Query cache.
 *
 * Modules call setupQueryInvalidation() in their setup() to automatically
 * invalidate queries when the backend pushes schema events.
 */

import type { QueryClient } from "@tanstack/react-query";
import type { AppTransport } from "../contracts/transport";

export interface InvalidationRule {
  readonly schemaIds: readonly string[];
  readonly queryKeys: readonly (readonly unknown[])[];
}

export function setupQueryInvalidation(
  transport: AppTransport,
  queryClient: QueryClient,
  rules: readonly InvalidationRule[],
): () => void {
  const unsubscribes: (() => void)[] = [];

  for (const rule of rules) {
    const unsub = transport.onSchemaEvent(rule.schemaIds, () => {
      for (const key of rule.queryKeys) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    });
    unsubscribes.push(unsub);
  }

  return () => {
    for (const unsub of unsubscribes) unsub();
  };
}

export function setupEventInvalidation(
  transport: AppTransport,
  queryClient: QueryClient,
  eventTypes: readonly string[],
  queryKeys: readonly (readonly unknown[])[],
): () => void {
  return transport.onEventType(eventTypes, () => {
    for (const key of queryKeys) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  });
}
